import firebase from "firebase/app";
import * as z from "zod/v4-mini";

import type { EventVideo } from "@spwn/types/firebase/firestore";

export type WithId<T> = T & { id: string };

// DocumentReferenceのスキーマ（Firestoreの参照型）
const documentReferenceSchema = z.object({
  id: z.string(),
  path: z.string(),
});

// EventVideoGroupのスキーマ
export const EventVideoGroupSchema = z.object({
  eventId: z.string(),
  name: z.string(),
  priority: z.number(),
  videoGroupsSetRef: documentReferenceSchema,
});

export type EventVideoGroup = z.infer<typeof EventVideoGroupSchema>;

// EventVideo（streaming/{eventId}/videos）のスキーマ
// EventVideo は多数のフィールドを持つため、検証に必要な最小限（_id が文字列であること）のみ
// チェックし、型としては @spwn/types の EventVideo をそのまま返す（as キャスト不要）。
export const EventVideoSchema = z.custom<EventVideo>(
  (value) =>
    typeof value === "object" &&
    value !== null &&
    typeof (value as { _id?: unknown })._id === "string"
);

// EventVideoGroupsSetのスキーマ
export const EventVideoGroupsSetSchema = z.object({
  /** 新規作成時に書き込まれる。既存データには無いため optional。 */
  eventId: z.optional(z.string()),
  name: z.string(),
  priority: z.number(),
  mainVideoRef: documentReferenceSchema,
  /**
   * VODの動画切り替え時に再生時間を同期するかどうかのフラグ
   * 何も設定していない場合にはtrueとし、同期が行なわれるようにする
   */
  syncPlaybackTimeOnSwitch: z._default(z.boolean(), true),
});

export type EventVideoGroupsSet = z.infer<typeof EventVideoGroupsSetSchema>;

export const FeatureFlagSchema = z.object({
  globalEnabled: z.boolean(),
  events: z.record(z.string(), z.boolean()),
});

export type FeatureFlag = z.infer<typeof FeatureFlagSchema>;

// 現地のもぎり時に使用チケットのグループを表すスキーマ
export const TicketRedeemGroupSchema = z.object({
  name: z.string(),
  // 手動もぎりができるかどうかのフラグ
  isTicketRedeemingEnabled: z.boolean(),
  ticketIds: z.array(z.string()),
  // /tickets/{eventId}/issuedTicketsに存在するデータをbrowserにcacheするために必要なeventIdのリスト
  eventIds: z.array(z.string()),
});

export type TicketRedeemGroup = z.infer<typeof TicketRedeemGroupSchema>;

// Placeのスキーマ（places/{placeId}）
// MorphCard 等で必要な表示用フィールドのみ厳格化し、それ以外は looseObject で許容
export const PlaceSchema = z.looseObject({
  areaName: z.string(),
  venueName: z.string(),
});

export type Place = z.infer<typeof PlaceSchema>;

// IssuedTicketのスキーマ（tickets/{eventId}/issuedTickets）
// QrcodeReaderで必要なフィールドのみバリデーションし、他はpassthroughで許容
export const IssuedTicketSchema = z.looseObject({
  authCode: z.optional(z.string()),
  isChecked: z.optional(z.boolean()),
  isRedeemed: z.optional(z.boolean()),
  ticketId: z.optional(z.string()),
  owner: z.string(),
});

export type IssuedTicket = z.infer<typeof IssuedTicketSchema>;

export const BlockedUserSchema = z.object({
  name: z.string(),
  createdAt: z.custom<firebase.firestore.Timestamp>(
    (value) => value instanceof firebase.firestore.Timestamp
  ),
  eventId: z.string(),
});

export type BlockedUser = z.infer<typeof BlockedUserSchema>;
